import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

const useBatchAssessmentState = ({
    students = [],
    skills = [],
    dokLevels = [],

    // Identifies the active Subject/Unit/Term/student-filter
    // request — e.g. `${subjectId}|${unitId}|${term}|${studentIdsKey}`.
    // Used below to tell a genuine filter change (safe to rebuild
    // every student from the server) apart from a same-context
    // refetch after a save (must not discard another student's
    // still-unsaved edits). Student IDs alone are not reliable
    // for this: a Term or Unit change can return the exact same
    // roster of students, which would otherwise look identical.
    contextKey = null,
}) => {
    const [studentState, setStudentState] = useState({});
    const [selectedStudentId, setSelectedStudentId] = useState(null);

    const previousContextKeyRef = useRef(null);

    // =========================================================
    // Build editable state fresh from the last-retrieved data,
    // discarding any local, unsaved edits.
    // =========================================================

    const buildStateFromStudents = useCallback(() => {
        const nextState = {};

        students.forEach((student) => {
            const entriesBySkill = {};

            skills.forEach((skill) => {
                entriesBySkill[skill.id] = false;
            });

            (student.entries || []).forEach((entry) => {
                entriesBySkill[entry.skill] = Boolean(
                    entry.is_achieved
                );
            });

            const dokProficiency = {};

            dokLevels.forEach((dok) => {
                dokProficiency[dok.level] = "";
            });

            (student.dok_assessments || []).forEach((item) => {
                dokProficiency[item.dok_level] =
                    item.proficiency || "";
            });

            nextState[student.student] = {
                student: student.student,
                progressCard: student.progress_card,
                exists: student.exists,

                dateUpdated:
                    student.date_updated || null,

                overallAssessment:
                    student.overall_assessment || "",

                // Assessment status is not stored — it is always
                // computed live from `entries` (see
                // getComputedAssessmentStatus), matching the
                // backend's server-side computation on save.

                educatorComment:
                    student.educator_comment || "",

                entries: entriesBySkill,

                dokProficiency,

                isDirty: false,
            };
        });

        return nextState;
    }, [
        students,
        skills,
        dokLevels,
    ]);


    // =========================================================
    // Initialize editable state whenever retrieved data changes
    //
    // This effect also re-runs after a successful save, because
    // saving triggers a refetch and `students` is a new array.
    // That refetch must NOT discard unsaved edits on students
    // who were not part of that save — only a genuine context
    // change (different Subject/Unit/Term/student filter) should
    // rebuild everyone from server state.
    // =========================================================

    useEffect(() => {
        if (!students.length) {
            setStudentState({});
            setSelectedStudentId(null);
            previousContextKeyRef.current =
                contextKey;
            return;
        }

        const isSameContext =
            previousContextKeyRef.current ===
            contextKey;

        previousContextKeyRef.current =
            contextKey;

        const freshState = buildStateFromStudents();

        if (!isSameContext) {
            // Genuinely new filter selection — safe to rebuild
            // every student from the server.
            setStudentState(freshState);

            // Do not auto-select the first student — the detail
            // panel should start blank ("select a student to
            // get started") until the teacher explicitly picks
            // one.
            setSelectedStudentId((current) =>
                current && freshState[current]
                    ? current
                    : null
            );

            return;
        }

        // Same context: this is a post-save refetch, not a
        // filter change. Preserve any student who still has
        // unsaved local edits; only sync students with no
        // pending edits to the latest server values.
        setStudentState((previousState) => {
            const nextState = {
                ...freshState,
            };

            Object.keys(previousState).forEach(
                (studentId) => {
                    if (
                        previousState[studentId]
                            ?.isDirty
                    ) {
                        nextState[studentId] =
                            previousState[
                                studentId
                            ];
                    }
                }
            );

            return nextState;
        });
    }, [
        students,
        buildStateFromStudents,
        contextKey,
    ]);


    // =========================================================
    // Discard all local, unsaved edits and revert every student
    // back to the last-retrieved server state.
    // =========================================================

    const resetToServerState = useCallback(() => {
        setStudentState(
            buildStateFromStudents()
        );
    }, [buildStateFromStudents]);

    // =========================================================
    // Clear the dirty flag for students that were just saved
    // successfully. Call this right after a successful save,
    // before the resulting refetch runs — it marks exactly the
    // saved students clean so the same-roster merge above (in
    // the init effect) syncs them to fresh server values instead
    // of mistaking them for still-unsaved edits.
    // =========================================================

    const markStudentsClean = useCallback(
        (studentIds = []) => {
            if (!studentIds.length) {
                return;
            }

            setStudentState((prev) => {
                const next = {
                    ...prev,
                };

                studentIds.forEach(
                    (studentId) => {
                        if (next[studentId]) {
                            next[studentId] = {
                                ...next[
                                    studentId
                                ],
                                isDirty: false,
                            };
                        }
                    }
                );

                return next;
            });
        },
        []
    );

    // =========================================================
    // Students with unsaved local edits — used to warn a
    // teacher before those edits are silently discarded
    // (mode switch, filter change, leaving the page).
    // =========================================================

    const dirtyStudentIds = useMemo(
        () =>
            Object.values(studentState)
                .filter((student) => student.isDirty)
                .map((student) => student.student),
        [studentState]
    );

    // =========================================================
    // Selected student
    // =========================================================

    const selectedStudent = useMemo(() => {
        if (!selectedStudentId) {
            return null;
        }

        return studentState[selectedStudentId] || null;
    }, [
        selectedStudentId,
        studentState,
    ]);

    // =========================================================
    // Generic student updater
    // =========================================================

    const updateStudent = useCallback(
        (studentId, updates) => {
            setStudentState((prev) => ({
                ...prev,
                [studentId]: {
                    ...prev[studentId],
                    ...updates,
                    isDirty: true,
                },
            }));
        },
        []
    );

    // =========================================================
    // Skill achievement
    // =========================================================

    const setSkillAchievement = useCallback(
        (
            studentId,
            skillId,
            isAchieved
        ) => {
            setStudentState((prev) => {
                const current = prev[studentId];

                if (!current) {
                    return prev;
                }

                return {
                    ...prev,
                    [studentId]: {
                        ...current,
                        entries: {
                            ...current.entries,
                            [skillId]: isAchieved,
                        },
                        isDirty: true,
                    },
                };
            });
        },
        []
    );

    // =========================================================
    // DOK proficiency
    // =========================================================

    const setDOKProficiency = useCallback(
        (
            studentId,
            dokLevel,
            proficiency
        ) => {
            setStudentState((prev) => {
                const current = prev[studentId];

                if (!current) {
                    return prev;
                }

                return {
                    ...prev,
                    [studentId]: {
                        ...current,
                        dokProficiency: {
                            ...current.dokProficiency,
                            [dokLevel]: proficiency,
                        },
                        isDirty: true,
                    },
                };
            });
        },
        []
    );

    // =========================================================
    // Overall assessment
    // =========================================================

    const setOverallAssessment = useCallback(
        (
            studentId,
            value
        ) => {
            updateStudent(
                studentId,
                {
                    overallAssessment: value,
                }
            );
        },
        [updateStudent]
    );

    // =========================================================
    // Educator comment
    // =========================================================

    const setEducatorComment = useCallback(
        (
            studentId,
            value
        ) => {
            updateStudent(
                studentId,
                {
                    educatorComment: value,
                }
            );
        },
        [updateStudent]
    );

    // =========================================================
    // Helpers
    // =========================================================

    const getAchievedSkillCount = useCallback(
        (studentId) => {
            const student = studentState[
                studentId
            ];

            if (!student) {
                return 0;
            }

            return Object.values(
                student.entries
            ).filter(Boolean).length;
        },
        [studentState]
    );
// =========================================================
// Apply Batch Entry to selected students
//
// Important:
// - Selected Skills are set to TRUE.
// - Unselected Skills are left unchanged.
// - Blank DOK proficiency values are ignored.
// - Blank overall assessment is ignored.
// - Nothing is saved to the backend here.
// =========================================================

const applyBatchChanges = useCallback(
    ({
        studentIds = [],
        selectedSkillIds = [],
        dokProficiency = {},
        overallAssessment = "",
    }) => {
        if (!studentIds.length) {
            return;
        }

        setStudentState((prev) => {
            const next = {
                ...prev,
            };

            studentIds.forEach((studentId) => {
                const current =
                    next[studentId];

                if (!current) {
                    return;
                }

                // -----------------------------------------
                // Preserve existing Skill state.
                // Only selected Batch Skills become true.
                // -----------------------------------------

                const nextEntries = {
                    ...current.entries,
                };

                selectedSkillIds.forEach(
                    (skillId) => {
                        nextEntries[skillId] =
                            true;
                    }
                );

                // -----------------------------------------
                // Preserve existing DOK proficiency.
                // Only non-blank Batch values overwrite.
                // -----------------------------------------

                const nextDOKProficiency = {
                    ...current.dokProficiency,
                };

                Object.entries(
                    dokProficiency
                ).forEach(
                    ([
                        dokLevel,
                        proficiency,
                    ]) => {
                        if (proficiency) {
                            nextDOKProficiency[
                                dokLevel
                            ] = proficiency;
                        }
                    }
                );

                next[studentId] = {
                    ...current,

                    entries:
                        nextEntries,

                    dokProficiency:
                        nextDOKProficiency,

                    overallAssessment:
                        overallAssessment ||
                        current.overallAssessment,

                    isDirty: true,
                };
            });

            return next;
        });
    },
    []
);

    return {
        studentState,
        selectedStudentId,
        selectedStudent,
        dirtyStudentIds,

        setSelectedStudentId,

        setSkillAchievement,
        setDOKProficiency,
        setOverallAssessment,
        setEducatorComment,
        applyBatchChanges,
        getAchievedSkillCount,
        resetToServerState,
        markStudentsClean,
    };
};

export default useBatchAssessmentState;